import { useHotkeys } from "react-hotkeys-hook";
import { Marker } from "react-map-gl/mapbox-legacy";
import { ActionButton } from "../../Common/Buttons/ActionButton";
import { Body } from "../../Common/Typography";
import { EventFeature, MapEvent } from "../LayeredMap";
import { useMapScrollIsolation } from "../utils/useMapScrollIsolation";
import {
  HeaderBar,
  MarkerBody,
  MenuCategory,
  MenuItems,
} from "./__styles__/clickObjectMenu";

export type MenuCategory = {
  header: string;
  objects: ObjectDisplayProps[];
};

const CategoryDisplay = ({ header, objects }: MenuCategory) => (
  <MenuCategory key={header}>
    <Body type="emphasis" size="small" color="contentPrimaryInverted">
      {header.toUpperCase()}
    </Body>
    {objects.map(ObjectDisplay)}
  </MenuCategory>
);

export type ObjectDisplayProps = {
  label: string;
  id: string;
  onClick: () => void;
};

const ObjectDisplay = ({ id, label, onClick }: ObjectDisplayProps) => (
  <Body
    type="emphasis"
    size="small"
    color="contentInfoInverted"
    style={{ cursor: "pointer", width: "fit-content" }}
    key={id}
  >
    <span onClick={onClick}>{label}</span>
  </Body>
);

export type ClickMenuDisplayConfig = {
  latitude: number;
  longitude: number;
  displayOnLeft: boolean;
  displayAbove: boolean;
};

type ClickObjectMenuProps = {
  menuDisplayConfig: ClickMenuDisplayConfig | null;
  time: number;
  menuCategories: MenuCategory[];
  closeMenu: (features?: EventFeature[], event?: MapEvent) => void;
} & Pick<
  ReturnType<typeof useMapScrollIsolation>,
  "onMouseEnter" | "onMouseLeave"
>;

const ClickObjectMenuLayer = ({
  menuDisplayConfig,
  time,
  menuCategories,
  closeMenu,
  ...scrollIsolationProps
}: ClickObjectMenuProps) => {
  useHotkeys("esc", e => {
    e.preventDefault();
    closeMenu();
  });

  if (!menuDisplayConfig) return null;
  const { latitude, longitude, displayOnLeft, displayAbove } =
    menuDisplayConfig;

  return (
    <Marker
      key={`click-object-menu-${time}`}
      latitude={latitude}
      longitude={longitude}
      offset={[displayOnLeft ? -10 : 10, displayAbove ? -10 : 10]}
      anchor={`${displayAbove ? "bottom" : "top"}-${displayOnLeft ? "right" : "left"}`}
    >
      <MarkerBody {...scrollIsolationProps}>
        <HeaderBar>
          <Body type="emphasis" size="small" color="contentPrimaryInverted">
            Objects
          </Body>
          <ActionButton
            icon="x"
            onClick={() => closeMenu()}
            aria-label="Close Object Menu"
            data-testid="close-icon"
            variant="inverted"
          />
        </HeaderBar>
        <MenuItems>{menuCategories.map(CategoryDisplay)}</MenuItems>
      </MarkerBody>
    </Marker>
  );
};

export default ClickObjectMenuLayer;
